import React, { useState } from 'react';
import { Row } from '@tanstack/react-table';
import { Button } from 'components/ui/button';
import { MoreVertical, Eye, Pencil, Ban, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { OrganizationData } from '../../services/organization-service';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Dialog } from 'components/ui/dialog';
import { EditOrganizationDetails } from '../edit-organization-details/edit-organization-details';

interface DataTableRowActionsProps {
    row: Row<OrganizationData>;
    onViewDetails: (org: OrganizationData) => void;
    onDeactivate?: (org: OrganizationData) => void;
    onActivate?: (org: OrganizationData) => void;
}

export function DataTableRowActions({
    row,
    onViewDetails,
    onDeactivate,
    onActivate,
}: Readonly<DataTableRowActionsProps>) {
    const { t } = useTranslation();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const handleItemClick = (action: (org: OrganizationData) => void, event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        action(row.original);
    };

    const handleEditClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setIsEditModalOpen(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-[160px]">
                    <DropdownMenuItem onClick={(e) => handleItemClick(onViewDetails, e)}>
                        <Eye className="mr-2 h-4 w-4" />
                        {t('VIEW_DETAILS')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleEditClick}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {t('EDIT')}
                    </DropdownMenuItem>
                    {row.original.isActive && onDeactivate && (
                        <DropdownMenuItem
                            onClick={(e) => handleItemClick(onDeactivate, e)}
                            className="text-error focus:text-error"
                        >
                            <Ban className="mr-2 h-4 w-4" />
                            {t('DEACTIVATE_ORGANIZATION')}
                        </DropdownMenuItem>
                    )}
                    {!row.original.isActive && onActivate && (
                        <DropdownMenuItem
                            onClick={(e) => handleItemClick(onActivate, e)}
                            className="text-success focus:text-success"
                        >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {t('ACTIVATE_ORGANIZATION')}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                {isEditModalOpen && (
                    <EditOrganizationDetails
                        organization={row.original}
                        onClose={handleEditModalClose}
                    />
                )}
            </Dialog>
        </>
    );
} 